/**
 * Aufgabe Erstellen/Bearbeiten - Erweitertes Formular
 * Felder: Titel, Frist, Priorität (3 Stufen), Betreuter, Maßnahme, Verantwortlicher
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams, useTabSearchParams } from '@/hooks/useTabRoute';
import { ArrowLeft, Save, Loader2, Upload, FileText, X, Paperclip, UserPlus, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
// Dialog imports removed - all forms are now inline
import { betreuungAPI } from '@/api/betreuungAPI';
import { authAPI } from '@/api/authAPI';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const PRIORITAETEN = [
  { value: 'dringend', label: 'Dringend', color: 'bg-red-500' },
  { value: 'mittel', label: 'Mittelfristig', color: 'bg-orange-400' },
  { value: 'niedrig', label: 'Gering', color: 'bg-green-500' },
];

export default function AufgabeFormPage() {
  const navigate = useNavigate();
  const { id } = useTabParams();
  const [searchParams] = useTabSearchParams();
  const { user } = useAuth();
  const isEdit = !!id;

  const [saving, setSaving] = useState(false);
  const [klienten, setKlienten] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [benutzer, setBenutzer] = useState([]);
  const [files, setFiles] = useState([]);
  const [quickPersonDialog, setQuickPersonDialog] = useState(false);
  const [quickPerson, setQuickPerson] = useState({ vorname: '', nachname: '', rolle: 'ansprechpartner' });
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    titel: '',
    beschreibung: '',
    klient_id: searchParams.get('klient') || '',
    faellig_am: '',
    prioritaet: 'mittel',
    status: 'offen',
    massnahme_id: '',
    massnahme_bezeichnung: '',
    verantwortlicher: '',
    kategorie: 'sonstiges',
    betreuter_name: '',
    zugewiesen_an_id: '',
    zugewiesen_an_name: '',
  });

  useEffect(() => {
    Promise.all([
      betreuungAPI.listKlienten(),
      betreuungAPI.listMassnahmen(),
      authAPI.getBenutzerAuswahl(),
    ]).then(([kl, ma, be]) => {
      setKlienten(kl);
      setMassnahmen(ma);
      setBenutzer(be || []);
    });

    if (isEdit) {
      betreuungAPI.getAufgabe(id).then(data => {
        setForm({
          titel: data.titel || '',
          beschreibung: data.beschreibung || '',
          klient_id: data.klient_id || '',
          faellig_am: data.faellig_am ? data.faellig_am.slice(0, 10) : '',
          prioritaet: data.prioritaet || 'mittel',
          status: data.status || 'offen',
          massnahme_id: data.massnahme_id || '',
          massnahme_bezeichnung: data.massnahme_bezeichnung || '',
          verantwortlicher: data.verantwortlicher || '',
          kategorie: data.kategorie || 'sonstiges',
          betreuter_name: data.betreuter_name || '',
          zugewiesen_an_id: data.zugewiesen_an_id || '',
          zugewiesen_an_name: data.zugewiesen_an_name || '',
        });
      });
    }
  }, [id, isEdit]);

  const handleKlientChange = (v) => {
    const klient = klienten.find(k => k.id === v);
    setForm(p => ({
      ...p,
      klient_id: v === 'none' ? '' : v,
      betreuter_name: klient ? `${klient.vorname} ${klient.nachname}` : '',
    }));
  };

  const handleMassnahmeChange = (v) => {
    const m = massnahmen.find(x => x.id === v);
    setForm(p => ({
      ...p,
      massnahme_id: v === 'none' ? '' : v,
      massnahme_bezeichnung: m ? (m.bezeichnung || m.typ) : '',
    }));
  };

  const handleBenutzerChange = (v) => {
    const b = benutzer.find(x => x.id === v);
    setForm(p => ({
      ...p,
      zugewiesen_an_id: v === 'none' ? '' : v,
      zugewiesen_an_name: b ? b.name : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titel) { toast.error('Bitte Titel eingeben'); return; }

    setSaving(true);
    try {
      const data = { ...form };
      if (!data.klient_id) { delete data.klient_id; delete data.betreuter_name; }
      if (!data.faellig_am) delete data.faellig_am;
      if (!data.massnahme_id) { delete data.massnahme_id; delete data.massnahme_bezeichnung; }
      if (!data.verantwortlicher) delete data.verantwortlicher;
      
      // Zuweisung: Wenn zugewiesen, füge zugewiesen_von und Zeitstempel hinzu
      if (data.zugewiesen_an_id) {
        data.zugewiesen_von_id = user?.id;
        data.zugewiesen_am = new Date().toISOString();
      } else {
        delete data.zugewiesen_an_id;
        delete data.zugewiesen_an_name;
        delete data.zugewiesen_von_id;
        delete data.zugewiesen_am;
      }

      if (isEdit) {
        await betreuungAPI.updateAufgabe(id, data);
        // Dateien hochladen
        for (const f of files) {
          await betreuungAPI.uploadDokument(f, { aufgabe_id: id, klient_id: data.klient_id || '' });
        }
        toast.success('Aufgabe aktualisiert');
        navigate(`/aufgaben/${id}`);
      } else {
        const result = await betreuungAPI.createAufgabe(data);
        // Dateien hochladen und mit der neuen Aufgabe verknüpfen
        for (const f of files) {
          await betreuungAPI.uploadDokument(f, { aufgabe_id: result.id, klient_id: data.klient_id || '' });
        }
        toast.success('Aufgabe erstellt' + (files.length ? ` mit ${files.length} Dokument(en)` : ''));
        navigate(`/aufgaben/${result.id}`);
      }
    } catch (e) {
      toast.error(e.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  // Schnell-Person anlegen
  const handleQuickPerson = async () => {
    if (!quickPerson.vorname || !quickPerson.nachname) {
      toast.error('Vor- und Nachname erforderlich');
      return;
    }
    try {
      await betreuungAPI.createNatuerlichePerson(quickPerson);
      toast.success(`${quickPerson.vorname} ${quickPerson.nachname} angelegt`);
      setQuickPersonDialog(false);
      setQuickPerson({ vorname: '', nachname: '', rolle: 'ansprechpartner' });
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="aufgabe-form-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEdit ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 space-y-4">
          {/* Titel */}
          <div>
            <Label>Titel *</Label>
            <Input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))}
              placeholder="z.B. Grundsicherung beantragen" data-testid="input-titel" />
          </div>

          {/* Beschreibung */}
          <div>
            <Label>Beschreibung</Label>
            <Textarea value={form.beschreibung} onChange={e => setForm(p => ({ ...p, beschreibung: e.target.value }))}
              rows={3} placeholder="Details zur Aufgabe..." data-testid="input-beschreibung" />
          </div>

          {/* Priorität - 3 Stufen visuell */}
          <div>
            <Label>Priorität</Label>
            <div className="flex gap-3 mt-1">
              {PRIORITAETEN.map(p => (
                <button key={p.value} type="button"
                  onClick={() => setForm(f => ({ ...f, prioritaet: p.value }))}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all border-2 ${
                    form.prioritaet === p.value
                      ? `${p.color} text-white border-transparent shadow-md scale-105`
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`prio-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Frist + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Frist</Label>
              <Input type="date" value={form.faellig_am}
                onChange={e => setForm(p => ({ ...p, faellig_am: e.target.value }))}
                data-testid="input-frist" />
            </div>
            {isEdit && (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offen">Offen</SelectItem>
                    <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                    <SelectItem value="wartend">Wartend</SelectItem>
                    <SelectItem value="erledigt">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Betreuter + Maßnahme */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Betreuter</Label>
              <Select value={form.klient_id || 'none'} onValueChange={handleKlientChange}>
                <SelectTrigger data-testid="select-klient"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Keiner --</SelectItem>
                  {klienten.map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Maßnahme / Bezug</Label>
              <Select value={form.massnahme_id || 'none'} onValueChange={handleMassnahmeChange}>
                <SelectTrigger data-testid="select-massnahme"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Keine --</SelectItem>
                  {massnahmen.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.bezeichnung || m.typ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Verantwortlicher + Zuweisung */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Verantwortlich (Freitext)</Label>
              <Input value={form.verantwortlicher}
                onChange={e => setForm(p => ({ ...p, verantwortlicher: e.target.value }))}
                placeholder="Name des Bearbeiters" data-testid="input-verantwortlicher" />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <UserCheck className="w-4 h-4" /> Zugewiesen an
              </Label>
              <Select value={form.zugewiesen_an_id || 'none'} onValueChange={handleBenutzerChange}>
                <SelectTrigger data-testid="select-zugewiesen">
                  <SelectValue placeholder="Benutzer auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Niemand --</SelectItem>
                  {benutzer.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      <div className="flex items-center gap-2">
                        <span>{b.name}</span>
                        <span className="text-xs text-slate-400">({b.rolle})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.zugewiesen_an_id && (
                <p className="text-xs text-blue-500 mt-1">
                  Benutzer wird über die Zuweisung benachrichtigt
                </p>
              )}
            </div>
          </div>

          {/* Dokumente anhängen */}
          <div>
            <Label className="flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Dokumente anhängen
            </Label>
            <div className="mt-2 space-y-2">
              {files.length > 0 && (
                <div className="space-y-1.5">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={fileInputRef} type="file" multiple className="hidden"
                onChange={e => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff,.xls,.xlsx,.txt,.eml,.msg"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                data-testid="attach-files-btn">
                <Upload className="w-4 h-4 mr-2" /> Dateien auswählen
              </Button>
              <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, Bilder, E-Mails</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => setQuickPersonDialog(true)}>
            <UserPlus className="w-4 h-4 mr-1" /> Person anlegen
          </Button>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </div>
      </form>

      {/* Quick Person Inline */}
      {quickPersonDialog && (
        <Card className="max-w-sm mx-auto mt-4 border-green-200 shadow-lg" data-testid="quick-person-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Schnell Person anlegen</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setQuickPersonDialog(false)}><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Vorname</Label>
                <Input value={quickPerson.vorname} onChange={e => setQuickPerson(p => ({ ...p, vorname: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Nachname</Label>
                <Input value={quickPerson.nachname} onChange={e => setQuickPerson(p => ({ ...p, nachname: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Rolle</Label>
              <Select value={quickPerson.rolle} onValueChange={v => setQuickPerson(p => ({ ...p, rolle: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ansprechpartner">Ansprechpartner</SelectItem>
                  <SelectItem value="angehoeriger">Angehöriger</SelectItem>
                  <SelectItem value="arzt">Arzt</SelectItem>
                  <SelectItem value="betreuer">Betreuer</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setQuickPersonDialog(false)}>Abbrechen</Button>
              <Button size="sm" onClick={handleQuickPerson}>Anlegen</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
